import { PlusCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import FormInputComponent from "@via/components/inputs/form-input-component";
import SelectComponent from "@via/components/select/select";
import {
  ApiConfigFormValues,
  defaultFieldMapping,
} from "@via/pages/projects/metadata/configuration/api-configs/utils/helper";
import { jsonKeyValidation } from "@via/utils/form-validators";
import { SelectOption } from "@via/utils/interfaces";
import { FC } from "react";
import { Control, FieldArrayPath, useFieldArray } from "react-hook-form";

interface MappingFieldsSectionProps {
  control: Control<ApiConfigFormValues>;
  name: FieldArrayPath<ApiConfigFormValues>;
  fieldOptions: SelectOption[];
  readOnly: boolean;
  addButtonLabel?: string;
}

const MappingFieldsSection: FC<MappingFieldsSectionProps> = ({
  control,
  name,
  fieldOptions,
  readOnly,
  addButtonLabel = "Insert Field",
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  return (
    <div className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="text-sm font-medium leading-6 text-gray-900">
          Field Mappings
        </label>
        {!readOnly && (
          <button
            type="button"
            onClick={() => append(defaultFieldMapping())}
            className="inline-flex shrink-0 items-center gap-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
            <PlusCircleIcon className="h-4 w-4" />
            {addButtonLabel}
          </button>
        )}
      </div>
      <div className="space-y-4">
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="grid min-w-0 grid-cols-1 gap-3 rounded-md border border-gray-200 p-3 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
            <div className="min-w-0">
              <label className="mb-2 flex items-center text-sm font-medium leading-6 text-gray-900">
                Field
              </label>
              <div className="mt-2">
                <SelectComponent
                  control={control}
                  name={`${name}.${index}.select`}
                  options={fieldOptions}
                  placeholder="Field"
                  enableClear={!readOnly}
                  readOnly={readOnly}
                  isSearchable
                />
              </div>
            </div>
            <div className="min-w-0 -mt-2">
              <FormInputComponent
                control={control}
                name={`${name}.${index}.rename`}
                label="Rename"
                placeholder="Rename"
                validations={jsonKeyValidation("Rename")}
                readOnly={readOnly}
              />
            </div>
            {!readOnly && (
              <div className="flex items-end xl:pb-0.5">
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="inline-flex rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-red-500">
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MappingFieldsSection;
